/**
 * User form validation utilities
 *
 * Extracted from useUserManagement.js for testability.
 * Contains pure validation functions.
 */

import * as Crypto from 'expo-crypto';

import { USER_MODES } from './constants';
import { t } from '../../i18n';

/**
 * Minimum password length requirement
 */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * Pick a uniformly random index in [0, max).
 *
 * Uses a CSPRNG rather than Math.random(): these values become temporary passwords, and
 * account creation relies on the invitation email being the only way to learn one. V8's
 * Math.random() is a xorshift128+ PRNG whose internal state can be recovered from a short
 * run of outputs, which is not a property a credential should depend on.
 *
 * Rejection sampling discards the tail of the byte range so the mapping onto `max` values
 * stays uniform (a plain `% max` would over-weight the first 256 % max symbols).
 *
 * @param {number} max - Exclusive upper bound, 1..256
 * @returns {number}
 */
function randomIndex(max) {
  const limit = Math.floor(256 / max) * max;
  let byte;

  do {
    byte = Crypto.getRandomBytes(1)[0];
  } while (byte >= limit);

  return byte % max;
}

/**
 * Generate a random temporary password
 * Creates a password with uppercase, lowercase, numbers, and special characters
 * @param {number} length - Password length (default: 12)
 * @returns {string} Generated password
 */
export function generateTemporaryPassword(length = 12) {
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // Excluded I, O for readability
  const lowercase = 'abcdefghjkmnpqrstuvwxyz'; // Excluded i, l, o for readability
  const numbers = '23456789'; // Excluded 0, 1 for readability
  const special = '!@#$%&*';
  const allChars = uppercase + lowercase + numbers + special;

  // Ensure at least one of each type
  const chars = [
    uppercase[randomIndex(uppercase.length)],
    lowercase[randomIndex(lowercase.length)],
    numbers[randomIndex(numbers.length)],
    special[randomIndex(special.length)],
  ];

  // Fill the rest randomly
  while (chars.length < length) {
    chars.push(allChars[randomIndex(allChars.length)]);
  }

  // Fisher-Yates shuffle so the guaranteed character classes are not positionally
  // predictable. (The previous sort(() => Math.random() - 0.5) was not a uniform shuffle
  // even setting the RNG aside — comparator-based shuffles are biased.)
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }

  return chars.join('');
}

/**
 * Validate email/username field
 * @param {string} email - Email to validate
 * @param {Object} options - Validation options
 * @param {string} options.mode - USER_MODES.create or USER_MODES.edit
 * @param {Object} options.existingUsers - Object of existing users keyed by email
 * @param {Object} options.authenticatedUser - Currently authenticated user
 * @returns {string|null} Error message or null if valid
 */
export function validateEmail(email, options = {}) {
  const { mode, existingUsers = {}, authenticatedUser } = options;
  const trimmedEmail = email?.trim() || '';

  if (!trimmedEmail) {
    return t('common:validation.emailRequired');
  }

  const lowerEmail = trimmedEmail.toLowerCase();

  if (mode === USER_MODES.create && lowerEmail in existingUsers) {
    return t('admin:users.userExists');
  }

  if (mode === USER_MODES.edit && !(lowerEmail in existingUsers)) {
    return t('admin:users.userNotFound');
  }

  if (authenticatedUser && authenticatedUser.email?.toLowerCase() === lowerEmail) {
    return t('admin:users.ownEmail');
  }

  return null;
}

/**
 * Validate password field
 * In production, passwords are auto-generated so validation is skipped.
 * In dev mode, passwords are optional (auto-generated if empty) but must meet min length if provided.
 * @param {string} password - Password to validate
 * @param {string} mode - USER_MODES.create or USER_MODES.edit
 * @returns {string|null} Error message or null if valid
 */
export function validatePassword(password, _mode) {
  const trimmedPassword = password?.trim() || '';
  const length = trimmedPassword.length;

  // In production, password is auto-generated so no validation needed
  if (typeof __DEV__ !== 'undefined' && !__DEV__) {
    return null;
  }

  // Dev mode: password is optional (auto-generated if empty) but must meet min length if provided
  if (length > 0 && length < MIN_PASSWORD_LENGTH) {
    return t('common:validation.passwordMinLength', { count: MIN_PASSWORD_LENGTH });
  }

  return null;
}

/**
 * Validate entire user form
 * @param {Object} formData - Form data to validate
 * @param {string} formData.email - Email/username
 * @param {string} formData.password - Password
 * @param {Object} options - Validation options
 * @param {string} options.mode - USER_MODES.create or USER_MODES.edit
 * @param {Object} options.existingUsers - Object of existing users
 * @param {Object} options.authenticatedUser - Currently authenticated user
 * @returns {Object} Object with errors and isValid flag
 */
export function validateUserForm(formData, options = {}) {
  const errors = {};

  const emailError = validateEmail(formData.email, options);
  if (emailError) {
    errors.email = emailError;
  }

  const passwordError = validatePassword(formData.password, options.mode);
  if (passwordError) {
    errors.password = passwordError;
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
  };
}
